// Attendance — clock-in/out records. Listed via POST attendance/attendance-list
// with server-side pagination + the confirmed filter params: dateFilter (range
// pills enum), status[] (checkedin/checkedout — its own vocabulary, no paused
// arm), groups[] and companyStructures[] (id arrays, omitted while empty).
// Still NO search or orderBy on the wire — no search box, nothing sortable.
// GET attendance/today-stats feeds the summary tiles and the live-locations
// list (checkedInUsers — confirmed shape).

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import type { ClockMediaDownloadItem, MediaType } from './mediaApi';

/** Punch lifecycle (backend ClockInState enum). */
export enum ClockInState {
  Started = 'started',
  Paused = 'paused',
  Ended = 'ended',
}

/** Which punch a photo belongs to. */
export type ClockMediaType = 'clock_in' | 'clock_out';

export interface AttendanceRecord {
  id: string;
  /** The person's id — the detail drawer's per-user fetch key. */
  userId: string;
  fullName: string;
  state: ClockInState;
  /** ISO. */
  clockInDate: string;
  /** ISO. Absent until the person clocks out — the cell dashes. */
  clockOutDate?: string;
  totalClockTimeSecs: number;
  /** ISO — the record's last write (punch or tracking ping), shown as "ago". */
  updatedAt: string;
  /** The punch photo, as the download endpoint wants it — absent when the
   *  punch carried none. */
  clockInPhoto?: ClockMediaDownloadItem;
  clockOutPhoto?: ClockMediaDownloadItem;
  /** Whatever the person typed at the punches — either may be absent. */
  clockInNotes?: string;
  clockOutNotes?: string;
  /** Punch coordinates + GPS accuracy (metres, as strings) — the drawer's
   *  Where card (clock-out point once ended, clock-in before). */
  location: { clockInLat: number; clockInLng: number; clockOutLat?: number; clockOutLng?: number };
  accuracy: { clockInAccuracy: string; clockOutAccuracy?: string };
}

export interface AttendancePage {
  items: AttendanceRecord[];
  total: number;
}

/** The toolbar range pills, verbatim wire values. */
export enum AttendanceDateFilter {
  Today = 'today',
  Last7Days = 'last_7_days',
  Last30Days = 'last_30_days',
  All = 'all',
}

/** The Status filter's own 2-value vocabulary — NOT ClockInState: the wire
 *  offers no paused arm here. */
export enum AttendanceStatusFilter {
  CheckedIn = 'checkedin',
  CheckedOut = 'checkedout',
}

export interface AttendanceQueryArgs {
  /** The page's range pills. The detail drawer's per-user fetch omits it
   *  entirely (full history) — hence optional. */
  dateFilter?: AttendanceDateFilter;
  /** Filter arrays are omitted (not sent empty) when nothing is selected. */
  status?: AttendanceStatusFilter[];
  groups?: string[];
  companyStructures?: string[];
  /** Narrow to specific people — the detail drawer sends [userId]. */
  users?: string[];
  page: number;
  limit: number;
}

/** A currently-clocked-in person, as the stats endpoint reports them — feeds
 *  the live-locations list (and the map pins via lastLocation). */
export interface CheckedInUser {
  companyUserId: string;
  fullName: string;
  state: ClockInState;
  /** ISO. */
  clockInDate: string;
  lastLocation: { latitude: number; longitude: number };
  /** ISO — the record's last write (punch or tracking ping). */
  updatedAt: string;
}

export interface AttendanceTodayStats {
  totalEmployees: number;
  totalCheckedIn: number;
  totalCheckedOut: number;
  checkedInUsers: CheckedInUser[];
}

// ---- raw shapes (per the confirmed response) ----
interface ApiClockMediaRef {
  media: { _id: string; mediaType: MediaType; imageName: string };
  mediaType: ClockMediaType;
}
interface ApiAttendanceItem {
  _id: string;
  company: string;
  user: string;
  firstName: string;
  lastName: string;
  /** Check-in AND check-out photos share this array, told apart by mediaType;
   *  [] when the punches carried no photo. */
  clockInMedia: ApiClockMediaRef[];
  clockInDate: string;
  clockOutDate?: string;
  totalClockTimeSecs: number;
  location: {
    clockInLat: number;
    clockInLng: number;
    clockOutLat?: number;
    clockOutLng?: number;
  };
  accuracy: { clockInAccuracy: string; clockOutAccuracy?: string };
  state: ClockInState;
  /** Whatever the person typed at either punch (the drawer's Note block) —
   *  the object and both fields are optional. */
  notes?: { clockInNotes?: string; clockOutNotes?: string };
  breakTimes: unknown[];
  trackLocation: unknown[];
  isDeleted: boolean;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  __v: number;
}
interface ApiAttendancePage {
  items: ApiAttendanceItem[];
  totalRecords: number;
}
interface ApiCheckedInUser {
  companyUserId: string;
  firstName: string;
  lastName: string;
  clockInDate: string;
  state: ClockInState;
  lastLocation: { latitude: number; longitude: number };
  updatedAt: string;
}
interface ApiTodayStats {
  totalEmployees: number;
  totalCheckedIn: number;
  totalCheckedOut: number;
  checkedInUsers: ApiCheckedInUser[];
}

export const toCheckedInUser = (u: ApiCheckedInUser): CheckedInUser => ({
  companyUserId: u.companyUserId,
  fullName: `${u.firstName} ${u.lastName}`.trim(),
  state: u.state,
  clockInDate: u.clockInDate,
  lastLocation: u.lastLocation,
  updatedAt: u.updatedAt,
});

/** The punch's photo as a ready download-request item (the payload wants the
 *  media's OWN kind, not the punch type). */
export const toDownloadItem = (
  media: ApiAttendanceItem['clockInMedia'],
  punch: ClockMediaType,
): ClockMediaDownloadItem | undefined => {
  const hit = media.find((m) => m.mediaType === punch)?.media;
  return hit && { mediaType: hit.mediaType, imageName: hit.imageName };
};

export const toAttendanceRecord = (a: ApiAttendanceItem): AttendanceRecord => ({
  id: a._id,
  userId: a.user,
  fullName: `${a.firstName} ${a.lastName}`.trim(),
  state: a.state,
  clockInDate: a.clockInDate,
  clockOutDate: a.clockOutDate,
  totalClockTimeSecs: a.totalClockTimeSecs,
  updatedAt: a.updatedAt,
  clockInPhoto: toDownloadItem(a.clockInMedia, 'clock_in'),
  clockOutPhoto: toDownloadItem(a.clockInMedia, 'clock_out'),
  clockInNotes: a.notes?.clockInNotes,
  clockOutNotes: a.notes?.clockOutNotes,
  location: a.location,
  accuracy: a.accuracy,
});

/** Wire page -> domain page (the totalRecords -> total rename lives here). */
export const toAttendancePage = (res: ApiAttendancePage): AttendancePage => ({
  items: res.items.map(toAttendanceRecord),
  total: res.totalRecords,
});

export const toTodayStats = (res: ApiTodayStats): AttendanceTodayStats => ({
  totalEmployees: res.totalEmployees,
  totalCheckedIn: res.totalCheckedIn,
  totalCheckedOut: res.totalCheckedOut,
  checkedInUsers: res.checkedInUsers.map(toCheckedInUser),
});

export const attendanceApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAttendanceList: build.query<AttendancePage, AttendanceQueryArgs>({
      query: (body) => ({ ...ROUTES.attendance.list, data: body }),
      transformResponse: toAttendancePage,
      providesTags: [{ type: 'Attendance', id: 'LIST' }],
    }),
    getAttendanceTodayStats: build.query<AttendanceTodayStats, void>({
      query: () => ({ ...ROUTES.attendance.todayStats }),
      transformResponse: toTodayStats,
      providesTags: [{ type: 'Attendance', id: 'STATS' }],
    }),
  }),
});

export const { useGetAttendanceListQuery, useGetAttendanceTodayStatsQuery } = attendanceApi;
